import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/businesses/fields/FieldInput";
import { BusinessEntity, useUpdateBusiness } from "@/hooks/useBusinesses";
import { wizardSchema, WizardValues } from "../wizard/schema";

interface Props {
  b: BusinessEntity;
}

const seed = (b: BusinessEntity): Partial<WizardValues> => ({
  ownerId: b.ownerId,
  businessName: b.businessName,
  nameEn: b.nameEn,
  nameMl: b.nameMl,
  address: b.address,
  city: b.city,
  pincode: b.pincode ?? "",
  phone: b.phone,
  lat: b.lat != null ? Number(b.lat) : undefined,
  lng: b.lng != null ? Number(b.lng) : undefined,
  careTypes: ((b.careTypes as string[]) ?? []).map((c) =>
    c.toLowerCase(),
  ) as any,
  amenityIds: (b.amenities ?? []).map((a) => a.amenityId),
  gstNumber: b.gstNumber ?? undefined,
  panNumber: b.panNumber ?? undefined,
  tradeLicenseNumber: b.tradeLicenseNumber ?? undefined,
  fssaiNumber: b.fssaiNumber ?? undefined,
  shopEstablishmentNumber: b.shopEstablishmentNumber ?? undefined,
  isMedicalProfessional: b.isMedicalProfessional ?? false,
  medicalRegistrationNo: b.medicalRegistrationNo ?? undefined,
  medicalCouncil: b.medicalCouncil ?? undefined,
  medicalQualifications: b.medicalQualifications ?? undefined,
});

export const ComplianceTab = ({ b }: Props) => {
  const form = useForm<WizardValues>({
    resolver: zodResolver(wizardSchema),
    mode: "onBlur",
    defaultValues: seed(b),
  });
  const update = useUpdateBusiness();
  const medical = form.watch("isMedicalProfessional");

  useEffect(() => {
    form.reset(seed(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);

  const onSave = form.handleSubmit(async (values) => {
    const patch: Partial<BusinessEntity> = {
      gstNumber: values.gstNumber || null,
      panNumber: values.panNumber || null,
      tradeLicenseNumber: values.tradeLicenseNumber || null,
      fssaiNumber: values.fssaiNumber || null,
      shopEstablishmentNumber: values.shopEstablishmentNumber || null,
      isMedicalProfessional: !!values.isMedicalProfessional,
      medicalRegistrationNo: values.medicalRegistrationNo || null,
      medicalCouncil: values.medicalCouncil || null,
      medicalQualifications: values.medicalQualifications || null,
    };
    try {
      await update.mutateAsync({ id: b.id, data: patch });
      toast.success("Compliance updated");
    } catch (e: any) {
      toast.error(e?.message ?? "Update failed");
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSave} className="space-y-6">
        <section className="bg-card rounded-card border border-border p-5 space-y-4">
          <h3 className="font-semibold">Business IDs</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <FieldInput<WizardValues> name="gstNumber" label="GST" autoUpper />
            <FieldInput<WizardValues> name="panNumber" label="PAN" autoUpper />
            <FieldInput<WizardValues>
              name="tradeLicenseNumber"
              label="Trade licence"
            />
            <FieldInput<WizardValues> name="fssaiNumber" label="FSSAI" />
            <FieldInput<WizardValues>
              name="shopEstablishmentNumber"
              label="Shop establishment"
            />
          </div>
        </section>

        <section className="bg-card rounded-card border border-border p-5 space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={!!medical}
              onCheckedChange={(v) =>
                form.setValue("isMedicalProfessional", v, { shouldDirty: true })
              }
            />
            Medical professional business
          </label>
          {medical ? (
            <div className="grid md:grid-cols-2 gap-3 border-l-2 border-primary pl-4">
              <FieldInput<WizardValues>
                name="medicalRegistrationNo"
                label="Registration no."
              />
              <FieldInput<WizardValues> name="medicalCouncil" label="Council" />
              <div className="md:col-span-2">
                <FieldInput<WizardValues>
                  name="medicalQualifications"
                  label="Qualifications"
                  textarea
                  rows={3}
                />
              </div>
            </div>
          ) : null}
        </section>

        {b.rejectionReason ? (
          <section className="bg-card rounded-card border border-destructive/30 bg-destructive/5 p-5">
            <h3 className="font-semibold mb-2 text-destructive">
              Rejection reason
            </h3>
            <pre className="text-sm whitespace-pre-wrap font-sans">
              {b.rejectionReason}
            </pre>
          </section>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset(seed(b))}
            disabled={!form.formState.isDirty || update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!form.formState.isDirty || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
